import { useEffect, useState } from 'react';
import api from '../api/axios';

/**
 * How many people report to the signed-in person.
 *
 * Needed in three places that all render at once on a phone — the sidebar, the
 * bottom bar and the home screen — because "can this person open the org
 * chart" is answered partly by whether they manage anybody, and that is only
 * knowable by asking the server.
 *
 * Three components asking the same question meant three identical requests on
 * every dashboard load. The in-flight promise is shared, so the second and
 * third callers wait on the first rather than starting their own; the answer
 * is then held for the rest of the session, since a reporting line does not
 * change while somebody is looking at their phone.
 *
 * Reset on sign-out so the next person does not inherit the last one's team.
 */
let cached = null;
let inFlight = null;

export const resetTeamSize = () => { cached = null; inFlight = null; };

const fetchTeamSize = () => {
  if (cached !== null) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = api.get('/my-team')
      .then((r) => { cached = (r.data?.team || []).length; return cached; })
      .catch(() => { cached = 0; return 0; })
      .finally(() => { inFlight = null; });
  }
  return inFlight;
};

export const useTeamSize = (user) => {
  const [size, setSize] = useState(cached ?? 0);

  useEffect(() => {
    if (!user) { resetTeamSize(); setSize(0); return undefined; }
    let alive = true;
    fetchTeamSize().then((n) => { if (alive) setSize(n); });
    return () => { alive = false; };
  }, [user]);

  return size;
};

export default useTeamSize;
